import { useEffect, useState } from "react";
import { CalendarDays, Plane, Users, Gauge, Cog, Activity, AlertTriangle, ExternalLink } from "lucide-react";
import api from "../lib/api";

export default function AiAircraftProfile({ code, name }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true); setError(false); setData(null);
    api.get(`/aircraft/${encodeURIComponent(code)}/article`, { timeout: 60000 })
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setError(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [code]);

  if (loading) return (
    <div className="border border-zinc-300 bg-white p-8 flex items-center gap-4" data-testid="ai-aircraft-loading">
      <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin shrink-0" />
      <div className="text-base text-zinc-700">We are creating a Profile for <span className="font-700 text-zinc-900">{name || "this aircraft"}</span>. This can take a few seconds.</div>
    </div>
  );
  if (error || !data) return <div className="border border-zinc-300 bg-white p-6 text-sm text-muted-foreground" data-testid="ai-aircraft-error">Could not generate a profile for this aircraft right now.</div>;

  const specs = [
    { icon: CalendarDays, label: "First flight", value: data.first_flight, color: "text-blue-600" },
    { icon: Plane, label: "Category", value: data.category, color: "text-violet-600" },
    { icon: Users, label: "Seating", value: data.seating, color: "text-emerald-600" },
    { icon: Gauge, label: "Range", value: data.range_km, color: "text-rose-600" },
    { icon: Cog, label: "Engines", value: data.engines, color: "text-orange-600" },
    { icon: Activity, label: "Status", value: data.status, color: "text-cyan-600" },
  ].filter((s) => s.value);

  return (
    <div className="border border-zinc-300 bg-white p-6 lg:p-8" data-testid="ai-aircraft-section">
      <div className="text-xs font-mono uppercase tracking-widest text-primary flex items-center gap-1.5 mb-2">
        <Plane className="w-3.5 h-3.5" /> TravelerPulse profile
      </div>
      {data.tagline && <p className="font-heading font-900 text-xl sm:text-2xl text-primary leading-snug mb-4">{data.tagline}</p>}
      {specs.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
          {specs.map((s) => (
            <div key={s.label} className="border-l-4 border-zinc-200 pl-3">
              <div className={`text-xs font-mono uppercase tracking-widest flex items-center gap-1 ${s.color}`}><s.icon className="w-3.5 h-3.5" />{s.label}</div>
              <div className="text-sm font-600 text-zinc-900 mt-0.5">{s.value}</div>
            </div>
          ))}
        </div>
      )}
      {data.overview && <div className="prose max-w-none whitespace-pre-line leading-relaxed text-zinc-800 text-[16px]">{data.overview}</div>}
      {data.notable_operators && (
        <div className="mt-5 border-l-4 border-blue-500 pl-3">
          <div className="font-heading font-900 text-lg text-blue-600">Notable operators</div>
          <div className="text-base text-zinc-800 mt-0.5">{data.notable_operators}</div>
        </div>
      )}
      {data.notable_facts?.length > 0 && (
        <div className="mt-5">
          <div className="font-heading font-900 text-emerald-600 text-lg mb-2">Notable facts</div>
          <ul className="space-y-1.5">
            {data.notable_facts.map((f, i) => <li key={i} className="flex gap-2 text-base text-zinc-800"><span className="text-emerald-600 font-mono font-700">›</span>{f}</li>)}
          </ul>
        </div>
      )}
      {data.links?.wikipedia && (
        <a href={data.links.wikipedia} target="_blank" rel="noreferrer"
          className="mt-5 inline-flex items-center gap-1.5 text-xs font-600 border border-zinc-300 px-3 py-1.5 hover:border-primary">
          <ExternalLink className="w-3.5 h-3.5" /> Read more on Wikipedia
        </a>
      )}
      <div className="mt-5 flex gap-2 items-start text-sm text-amber-700 bg-amber-50 border border-amber-200 p-3">
        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /><span>{data.disclaimer}</span>
      </div>
    </div>
  );
}
